/**
 * Agenda → CSV (US-MB-5 export).
 *
 * REUSE NOTE: `@skillab-tech/scrape-kit` ships an RFC-4180 + formula-injection-safe serializer and
 * the second-consumer rule points at it. Not adopted here, deliberately: Klapzo consumes no
 * `@skillab-tech/*` package yet, so this would be the repo's first private-registry dependency —
 * which means wiring `NODE_AUTH_TOKEN` into Workers Builds before anything can build — and
 * scrape-kit is a Document-dependent scraping toolkit being pulled into a pure, zero-dependency
 * domain package for fifteen lines. Revisit when Klapzo adopts the shared libs properly; the
 * escaping rules below are deliberately the same ones.
 */
import type { ScheduledAgenda } from "./types.js";

/**
 * RFC 4180 quoting PLUS a leading apostrophe on anything a spreadsheet would evaluate.
 *
 * Method and block titles are user input that lands in Excel/Sheets, so a title of
 * `=HYPERLINK("http://evil","click")` is a live formula in the recipient's spreadsheet, not text.
 * `\t` and `\r` are in the set because both let a payload sneak past a naive `=+-@` check.
 */
export function csvCell(value: string | number): string {
  const raw = String(value);
  const guarded = /^[=+\-@\t\r]/.test(raw) ? `'${raw}` : raw;
  return /[",\n\r]/.test(guarded) ? `"${guarded.replace(/"/g, '""')}"` : guarded;
}

export interface CsvHeaders {
  order: string;
  start: string;
  title: string;
  duration: string;
  total: string;
}

/**
 * Emits a UTF-8 BOM. Without it Excel on Windows reads the file as the local codepage and every
 * pt-BR accent in a method title arrives mojibake — the single most likely way this export gets
 * reported as broken.
 */
export function agendaToCsv(agenda: ScheduledAgenda, headers: CsvHeaders): string {
  const rows = [
    [headers.order, headers.start, headers.title, headers.duration],
    ...agenda.blocks.map((b, i) => [i + 1, b.startClock ?? "", b.title, b.durationMin]),
    [], // blank separator, so the total does not read as another block
    ["", "", headers.total, agenda.totalMin],
  ];
  return `﻿${rows.map((r) => r.map(csvCell).join(",")).join("\r\n")}\r\n`;
}
